import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api, API } from "@/lib/api";

const Ctx = createContext(null);
export const useProducts = () => useContext(Ctx);

/** Turn a media library url ("/api/media/{id}") into a fully-qualified image URL. */
const absolute = (url) => {
  if (!url) return url;
  if (url.startsWith("http")) return url;
  if (url.startsWith("/api/")) return API.replace(/\/api$/, "") + url;
  return url;
};
const normImages = (arr) => (arr || []).map(absolute).filter(Boolean);

export const ProductsProvider = ({ children }) => {
  // Start empty so no baked-in/demo product image can ever paint before the API response.
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [p, c] = await Promise.all([
        api.get("/products").catch(() => ({ data: [] })),
        api.get("/categories").catch(() => ({ data: [] })),
      ]);
      setProducts(Array.isArray(p.data) ? p.data.map(x => ({ ...x, images: normImages(x.images) })) : []);
      setCategories(Array.isArray(c.data) ? c.data.map(x => ({ ...x, image: absolute(x.image) })) : []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const findProduct = useCallback((slug) => products.find(p => p.slug === slug), [products]);
  const bySection = useCallback((s) => products.filter(p => p.section?.includes(s)), [products]);
  const byCategory = useCallback((slug) => products.filter(p => p.category === slug), [products]);

  const value = useMemo(() => ({
    products, categories, loading, findProduct, bySection, byCategory, refresh,
  }), [products, categories, loading, findProduct, bySection, byCategory, refresh]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};
